<template>
  <div class="bg-gray-200 pb-12">
    <nav class="bg-slate-300 gap-2 py-4 px-4 mb-10 flex justify-between items-center max-sm:flex-col">
      <div
        class="inline-flex w-max items-center gap-x-1 px-2 uppercase bg-white rounded-lg border border-slate-900/40 focus-within:ring-1 focus-within:ring-teal-500 max-sm:w-full">
        <SvgIcon name="folders-fill" classes="text-teal-600 size-5 shrink-0" />
        <select id="school-select" v-model="selectedSchool" @change="handleSchoolChange"
          class="form-select block w-full focus:border-teal-300 focus:ring-opacity-50 border-none pl-0 focus:outline-none focus:ring-0"
          aria-label="Select a school">
          <option value="" disabled>Choose a school...</option>
          <option v-for="school in schools" :key="school.slug" :value="school.slug"
            :selected="schoolslug === school.slug">
            {{ school.title }}
          </option>
        </select>
      </div>

      <div class="inline-flex w-max items-center gap-x-1 ml-auto uppercase max-sm:hidden">
        <label for="sort-select" class="text-sm text-slate-600 font-medium whitespace-nowrap">Sort by</label>
        <select id="sort-select" v-model="sortBy"
          class="form-select block bg-transparent rounded-lg w-full text-slate-700 uppercase font-medium focus:border-teal-300 focus:ring-opacity-50 border-none focus:outline-none focus:ring-1">
          <option value="default">Default</option>
          <option value="title">Title</option>
          <option value="date">Date</option>
        </select>
      </div>
    </nav>

    <transition name="fade">
      <p v-if="successMessage" class="border border-teal-700 bg-teal-500 px-4 py-3 mx-4 mb-8 text-lg text-white"
        v-html="successMessage">
      </p>
    </transition>
    <transition name="fade">
      <p v-if="errorMessage" class="border border-red-900 bg-red-700 p-2 text-lg text-white">
        There was an error downloading your files: {{ errorMessage }}
      </p>
    </transition>

    <transition name="fade">
      <div v-if="selectedFiles.length"
        class="fixed z-50 bottom-0 left-0 text-white bg-teal-600 border-t border-teal-700 w-full p-4">
        <div class="max-w-6xl mx-auto flex flex-wrap gap-2 items-center justify-between">
          <p v-if="!downloading" class="mb-0">{{ selectedFiles.length }} Total Files.</p>
          <p v-if="downloading" class="mb-0">Your files are being prepared. This can take up to several minutes. Your
            download will automatically begin when it is ready.</p>

          <button v-if="!downloading" @click="selectedFiles = []" class="ml-auto btn btn--secondary">Clear
            Files</button>

          <button @click.prevent="downloadFiles" class="btn bg-teal-700" :disabled="downloading">
            <span v-if="!downloading">Download All Files</span>
            <vue-simple-spinner v-else size="small" line-bg-color="#fff" line-fg-color="#5ca0ea" />
          </button>

          <vue-simple-progress v-if="downloading" :val="progress" bar-color="#5ca0ea" class="mt-2 mb-4 w-full" />
        </div>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
      <ul v-if="school.modules.data.length" class="grid px-4 gap-4 md:grid-cols-2">
        <asset-browser-module v-for="module in filteredSchool" :key="module.id" :module="module"
          :selectedFileCount="selectedFiles.length" :downloadFiles="downloadFiles" :downloading="downloading" />
      </ul>
      <vue-simple-spinner v-else message="Loading courses..." size="large" line-bg-color="#eee" class="text-center"
        line-fg-color="#05b284" />
    </transition>
  </div>
</template>

<script>
import AssetBrowserModule from './AssetBrowserModule.vue'
import VueSimpleProgress from '@js/components/Progress.vue'
import zipAssetFiles from '@js/lib/zipAssetFiles.js'
import SvgIcon from './SvgIcon.vue'

let progressInterval = null

export default {
  name: 'assetBrowser',

  props: {
    schoolslug: {
      type: String,
      required: true
    },
    schools: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      downloading: false,
      successMessage: null,
      errorMessage: null,
      school: {
        modules: {
          data: []
        }
      },
      selectedFiles: [],
      progress: 5,
      selectedSchool: this.schoolslug,
      sortBy: 'default',
    }
  },

  computed: {
    // Filters out modules with children to avoid duplicate downloads and sorts them
    filteredSchool() {
      if (!this.school.modules || !this.school.modules.data) {
        return []
      }
      let modules = this.school.modules.data.filter(module => !module.hasDescendants);

      if (this.sortBy === 'title') {
        return modules.slice().sort((a, b) => {
          if (!a.title || !b.title) return 0;
          return a.title.localeCompare(b.title);
        });
      } else if (this.sortBy === 'date') {
        // Use dateUpdated.date for sorting, fallback to 0 if missing
        return modules.slice().sort((a, b) => {
          const dateA = a.dateUpdated && a.dateUpdated.date ? new Date(a.dateUpdated.date) : new Date(0);
          const dateB = b.dateUpdated && b.dateUpdated.date ? new Date(b.dateUpdated.date) : new Date(0);
          return dateB - dateA;
        });
      } else if (this.sortBy === 'default') {
        // Preserve server order
        return modules;
      }
      return modules;
    }
  },

  components: {
    AssetBrowserModule,
    VueSimpleProgress,
    SvgIcon
  },

  created() {
    fetch(`/api/v1/school/${this.schoolslug}?include=modules`)
      .then(response => response.json())
      .then(data => this.school = data);
  },

  mounted() {
    this.emitter.on('selectfile', this.onSelectFile);
    this.emitter.on('zipFilesError', this.downloadError);
    this.emitter.on('zipFilesSuccess', this.downloadSuccess);
  },

  beforeUnmount() {
    this.emitter.off('selectfile', this.onSelectFile);
    this.emitter.off('zipFilesError', this.downloadError);
    this.emitter.off('zipFilesSuccess', this.downloadSuccess);
  },

  methods: {
    // addToArray is a boolean
    onSelectFile(obj) {
      if (obj.checked) {
        this.selectedFiles.push(obj.file)
      } else {
        const idx = this.selectedFiles.indexOf(obj.file)

        if (idx > -1) {
          this.selectedFiles.splice(idx, 1)
        }
      }
    },

    downloadFiles() {
      this.downloading = true
      this.errorMessage = null
      this.successMessage = null
      this.progress = 5 // reset

      let files = this.selectedFiles.map((file) => {
        return (file.folder || '') + file.filename
      })

      zipAssetFiles.zipFiles(files)

      // fake progress bar
      progressInterval = setInterval(() => {
        this.progress = this.progress + Math.ceil(Math.random() * 4)

        if (this.progress >= 85) {
          clearInterval(progressInterval)
        }
      }, 2500)
    },

    downloadError(err) {
      this.downloading = false
      this.errorMessage = err
      this.progress = 5

      clearInterval(progressInterval)
    },

    downloadSuccess(fileUrl) {
      this.selectedFiles = []
      this.progress = 100
      clearInterval(progressInterval)

      // pause for fake progress bar
      setTimeout(() => {
        this.downloading = false
        this.errorMessage = null
        window.location = fileUrl
        this.successMessage = `Your files should start downloading immediately. If it doesn't <a class="text-white" href="${fileUrl}" download>click here</a>`
      }, 1500);
    },

    handleSchoolChange() {
      if (this.selectedSchool) {
        window.location.href = `/asset-browser/${this.selectedSchool}`
      }
    }
  }
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.21, 0, 0.36, 1);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
